import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerService, shipmentService } from '../services/firebase';
import { Customer, ShipmentForm as ShipmentFormType } from '../types';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

const initialState: ShipmentFormType = {
  customerId: '',
  description: '',
  origin: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
  },
  destination: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
  },
  weight: 0,
  dimensions: {
    length: 0,
    width: 0,
    height: 0,
  },
};

const ShipmentForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formData, setFormData] = useState<ShipmentFormType>(initialState);
  const [photos, setPhotos] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const fetchedCustomers = await customerService.getAllCustomers();
        setCustomers(fetchedCustomers);
      } catch (err) {
        setError('Failed to fetch customers.');
        console.error(err);
      }
    };
    fetchCustomers();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'file' && name === 'photos') {
      const fileInput = e.target as HTMLInputElement;
      if (fileInput.files) {
        setPhotos(Array.from(fileInput.files));
      }
      return;
    }
    const keys = name.split('.');

    if (keys.length > 1) {
      setFormData(prev => {
        const updated = { ...prev };
        let current: any = updated;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updated;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('You must be logged in to create a shipment.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Upload all photos and get their URLs
      let photoURLs: string[] = [];
      for (const file of photos) {
        const storageRef = ref(storage, `shipments/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        photoURLs.push(url);
      }

      const shipmentPayload: ShipmentFormType = {
        ...formData,
        weight: Number(formData.weight),
        dimensions: {
          length: Number(formData.dimensions.length),
          width: Number(formData.dimensions.width),
          height: Number(formData.dimensions.height),
        },
        photos: photoURLs,
      };

      await shipmentService.createShipment(shipmentPayload, user.id);
      navigate('/shipments');
    } catch (err) {
      setError('Failed to create shipment. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Shipment Details</h2>

      {error && <p className="text-red-500 bg-red-100 p-3 rounded mb-4">{error}</p>}

      <div className="mb-6">
        <label htmlFor="customerId" className="block text-gray-700 text-sm font-bold mb-2">Customer</label>
        <select
          id="customerId"
          name="customerId"
          value={formData.customerId}
          onChange={handleChange}
          required
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        >
          <option value="" disabled>Select a customer</option>
          {customers.map(customer => (
            <option key={customer.id} value={customer.id}>{customer.name}</option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <label htmlFor="description" className="block text-gray-700 text-sm font-bold mb-2">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={3}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
        <fieldset className="border p-4 rounded-lg">
          <legend className="text-lg font-semibold px-2">Origin</legend>
          <div className="space-y-4">
            <input name="origin.address" value={formData.origin.address} onChange={handleChange} placeholder="Street Address" required className="w-full p-2 border rounded" />
            <input name="origin.city" value={formData.origin.city} onChange={handleChange} placeholder="City" required className="w-full p-2 border rounded" />
            <div className="flex gap-4">
              <input name="origin.state" value={formData.origin.state} onChange={handleChange} placeholder="State" required className="w-1/2 p-2 border rounded" />
              <input name="origin.zipCode" value={formData.origin.zipCode} onChange={handleChange} placeholder="Zip Code" required className="w-1/2 p-2 border rounded" />
            </div>
            <input name="origin.country" value={formData.origin.country} onChange={handleChange} placeholder="Country" required className="w-full p-2 border rounded" />
          </div>
        </fieldset>

        <fieldset className="border p-4 rounded-lg">
          <legend className="text-lg font-semibold px-2">Destination</legend>
          <div className="space-y-4">
            <input name="destination.address" value={formData.destination.address} onChange={handleChange} placeholder="Street Address" required className="w-full p-2 border rounded" />
            <input name="destination.city" value={formData.destination.city} onChange={handleChange} placeholder="City" required className="w-full p-2 border rounded" />
            <div className="flex gap-4">
              <input name="destination.state" value={formData.destination.state} onChange={handleChange} placeholder="State" required className="w-1/2 p-2 border rounded" />
              <input name="destination.zipCode" value={formData.destination.zipCode} onChange={handleChange} placeholder="Zip Code" required className="w-1/2 p-2 border rounded" />
            </div>
            <input name="destination.country" value={formData.destination.country} onChange={handleChange} placeholder="Country" required className="w-full p-2 border rounded" />
          </div>
        </fieldset>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
        <fieldset className="border p-4 rounded-lg">
            <legend className="text-lg font-semibold px-2">Package Details</legend>
            <div className="mb-4">
                <label htmlFor="weight" className="block text-sm font-bold mb-1">Weight (kg)</label>
                <input type="number" name="weight" id="weight" value={formData.weight} onChange={handleChange} required className="w-full p-2 border rounded" />
            </div>
            <div>
                <label className="block text-sm font-bold mb-1">Dimensions (cm)</label>
                <div className="flex gap-4">
                    <input type="number" name="dimensions.length" value={formData.dimensions.length} onChange={handleChange} placeholder="L" required className="w-1/3 p-2 border rounded" />
                    <input type="number" name="dimensions.width" value={formData.dimensions.width} onChange={handleChange} placeholder="W" required className="w-1/3 p-2 border rounded" />
                    <input type="number" name="dimensions.height" value={formData.dimensions.height} onChange={handleChange} placeholder="H" required className="w-1/3 p-2 border rounded" />
                </div>
            </div>
            <div className="mt-4">
              <label htmlFor="photos" className="block text-sm font-bold mb-1">Upload Photos (optional, multiple allowed)</label>
              <input type="file" name="photos" id="photos" accept="image/*" multiple onChange={handleChange} className="w-full p-2 border rounded" />
            </div>
        </fieldset>
      </div>

      <div className="flex items-center justify-end">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg focus:outline-none focus:shadow-outline disabled:bg-gray-400 flex items-center"
        >
          {loading && <LoadingSpinner />}
          {loading ? 'Creating...' : 'Create Shipment'}
        </button>
      </div>
    </form>
  );
};

export default ShipmentForm; 